<template>
  <div class="sort">
    <button
      v-show="enableDownload"
      v-tooltip.top="capitalize($t('actions.download'))"
      class="btn btn-default btn-sm btn-no-text download-list-as-csv"
      @click="onDownloadClicked">
      <icon-download />
    </button>
    <multiselect
      v-model="sortDirectionInternal"
      class="sort-direction"
      :options="sortDirectionOptions"
      :multiple="false"
      label="label"
      track-by="label"
      :allow-empty="false"
      :show-labels="false"
      :placeholder="null"
      :searchable="true" />
    <multiselect
      v-model="sortByInternal"
      class="sort-by"
      :options="sortByOptions"
      :multiple="false"
      label="label"
      track-by="label"
      :allow-empty="false"
      :show-labels="false"
      :placeholder="null"
      :searchable="true" />
    <span class="icon"><icon-sort /></span>
  </div>
</template>

<script setup lang="ts">
  //#region Imports

  import { capitalize, onMounted, Ref, ref, watch } from 'vue';

  import { IconDownload, IconSort } from '@/common/icons/monochrome';
  import { ISortBy, ISortDirection } from '@/org-app/components/packages/DealSorting';

  //#endregion

  //#region Props

  interface ITilesSortControlProps {
    sortDirection?: ISortDirection;
    sortBy?: ISortBy;
    sortDirectionOptions: ISortDirection[];
    sortByOptions: ISortBy[];
    enableDownload: boolean;
  }

  const props = withDefaults(defineProps<ITilesSortControlProps>(), {
    enableDownload: false,
    sortBy: null,
    sortDirection: null,
  });

  //#endregion

  //#region Data
  interface ITileSortControlEmits {
    (e: 'sortByChanged', value: ISortBy);
    (e: 'sortDirectionChanged', value: ISortDirection);
    (e: 'downloadClicked');
  }

  const emits = defineEmits<ITileSortControlEmits>();

  const sortByInternal: Ref<ISortBy> = ref(null);
  const sortDirectionInternal: Ref<ISortDirection> = ref(null);

  //#endregion

  //#region methods

  function onDownloadClicked() {
    emits('downloadClicked');
  }

  //#endregion

  //#region Watches

  watch(
    () => props.sortBy,
    () => {
      sortByInternal.value = props.sortBy;
    },
  );

  watch(
    () => props.sortDirection,
    () => {
      sortDirectionInternal.value = props.sortDirection;
    },
  );

  watch(
    () => sortDirectionInternal.value,
    () => {
      emits('sortDirectionChanged', sortDirectionInternal.value);
    },
  );

  watch(
    () => sortByInternal.value,
    () => {
      emits('sortByChanged', sortByInternal.value);
    },
  );

  //#region Lifecycle hooks

  onMounted(() => {
    sortByInternal.value = props.sortBy;
    sortDirectionInternal.value = props.sortDirection;
  });

  //#endregion
</script>

<style lang="scss" scoped>
  @use '@/styles/variables.scss' as *;
  .sort {
    display: flex;
    flex-grow: 1;
    flex-direction: row-reverse;
    align-items: center;

    .multiselect {
      margin-left: 0.8rem;
    }

    .sort-by {
      width: 15rem;
    }

    .sort-direction {
      width: 13rem;
    }

    .icon {
      fill: var(--textMutedColor);
      stroke: var(--textMutedColor);

      svg {
        width: 2rem;
      }
    }

    .download-list-as-csv {
      margin-left: 0.8rem;
    }
  }
</style>
